import http, { request } from "@config/http";
import { Page, Pagination, List } from "@services/common/model/page.model";
import {
  SequenceCdrPayloadModel,
  JobModel,
  JobHistoryModel,
  JobHistoryPayloadModel,
  ProteinBinderDesignPayloadModel,
  BatchDownloadModel,
  TaskResultModel,
  TokenInfoModel,
  VhhDesignPayloadModel,
  DockingPayloadModel,
  CheckCoordPayloadModel,
  CheckCoordResultModel,
  CheckDockingCoordPayloadModel,
  CheckDockingCoordResultModel,
  FileContentConvertPayloadModel,
  FileContentConvertResultModel,
  AntibodyDesignParamsConvertPayloadModel,
  PeptideDesignPayloadModel,
  ExampleModel,
} from "@services/home/model/home.model";
import { JobTypeEnum } from "../home/enum/home.enum";
import type { ResponseType, AxiosProgressEvent } from "axios";

export const batchGetCdrInfoApi = async (params: SequenceCdrPayloadModel) => {
  const { data } = await http.post<null, Page<List<number[] | null>>>(
    `lite/util/batch_is_cdr`,
    {
      ...params,
    }
  );

  return data.list;
};

export const getJobDetailApi = async (job_id: string, signal?: AbortSignal) => {
  const { data } = await http.get<null, Page<JobModel>>(
    `lite/job_base/base_info/${job_id}`,
    {
      signal,
    }
  );

  return data;
};

export const getJobListApi = async (params: JobHistoryPayloadModel) => {
  const { data } = await http.get<null, Page<Pagination<JobHistoryModel>>>(
    `lite/job_base/list_by_type`,
    {
      params,
    }
  );

  return data;
};

export const createProteinBinderDesignApi = async (
  params: ProteinBinderDesignPayloadModel
) => {
  return http.post<null, Page<{ job_id: string }>>(
    `lite/job/v2/protein_binder_design/create`,
    {
      ...params,
    }
  );
};

export const renameJobNameApi = async (job_id: string, job_name: string) => {
  return http.post<null, Page<{ job_id: string }>>(
    `lite/job_base/rename/${job_id}`,
    {
      job_name,
    }
  );
};

export const checkNeedAsyncDownloadApi = (params: BatchDownloadModel) => {
  return http.post<null, Page<{ need_async_download: boolean }>>(
    `lite/file/is_need_async_download`,
    {
      ...params,
    }
  );
};

export const downloadFileApi = async (
  fids: string[],
  prefetch?: string,
  responseType?: ResponseType,
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
) => {
  return request.post<null, Blob | string | Page<null>>(
    `lite/file/download_file`,
    { fids },
    {
      responseType,
      headers: {
        prefetch,
      },
      onDownloadProgress: (progressEvent) => {
        if (onDownloadProgress) {
          onDownloadProgress(progressEvent);
        }
      },
    }
  );
};

export const batchDownloadFileUrlApi = (params: BatchDownloadModel) => {
  return http.post<null, Page<{ id: string }>>(
    `lite/file/async_batch_download`,
    {
      ...params,
    }
  );
};

export const asyncDownloadTaskInfoApi = async (
  id: string,
  signal?: AbortSignal
) => {
  const { data } = await http.get<null, Page<TaskResultModel>>(
    `lite/file/async_download_task_info`,
    {
      params: {
        id,
      },
      signal,
    }
  );

  return data;
};

export const getProteinBinderDesignTokenInfoApi = async (
  params: ProteinBinderDesignPayloadModel
) => {
  const { data } = await request.post<
    null,
    Page<{ token_info: TokenInfoModel }>
  >(`lite/job/v2/protein_binder_design/token_info`, {
    ...params,
  });

  return data.token_info;
};

export const createVhhDesignApi = async (params: VhhDesignPayloadModel) => {
  return http.post<null, Page<{ job_id: string }>>(
    `lite/job/v2/antibody_design/create`,
    {
      ...params,
    }
  );
};

export const getVhhDesignTokenInfoApi = async (
  params: VhhDesignPayloadModel
) => {
  const { data } = await request.post<
    null,
    Page<{ token_info: TokenInfoModel }>
  >(`lite/job/v2/antibody_design/token_info`, {
    ...params,
  });

  return data.token_info;
};

export const getFoldSequenceStructureApi = async (text: string) => {
  const response = await fetch(
    `https://api.esmatlas.com/foldSequence/v1/pdb/`,
    {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
      },
      body: text,
    }
  );

  if (response.status !== 200) {
    const res = await response.text();

    throw new Error(res);
  }

  const blob = await response.blob();

  const file = new File([blob], `test.pdb`);

  return file;
};

export const createDockingJobApi = async (params: DockingPayloadModel) => {
  return http.post<null, Page<{ job_id: string }>>(
    `lite/job/v2/dock/ab_ag_dock_lite/create`,
    {
      ...params,
    }
  );
};

export const getDockingTokenInfoApi = async (params: DockingPayloadModel) => {
  const { data } = await request.post<
    null,
    Page<{ token_info: TokenInfoModel }>
  >(`lite/job/v2/dock/ab_ag_dock_lite/token_info`, {
    ...params,
  });

  return data.token_info;
};

export const getExamplesApi = async (
  type_name: JobTypeEnum,
  binder_type?: string
) => {
  return http.get<null, Page<List<ExampleModel>>>(`lite/job_base/examples`, {
    params: {
      type_name,
      binder_type,
    },
  });
};

export const checkLigandCCDApi = (ccd_code_list: string[]) => {
  return http.post<null, Page<{ id: string }>>(`lite/util/batch_ccd_check`, {
    ccd_code_list,
  });
};

export const checkCoordValidityApi = (params: CheckCoordPayloadModel) => {
  return http.post<null, Page<CheckCoordResultModel>>(
    `lite/job/v2/protein_binder_design/check_coord_info`,
    {
      ...params,
    }
  );
};

export const checkDockingCoordValidityApi = (
  params: CheckDockingCoordPayloadModel
) => {
  return http.post<null, Page<CheckDockingCoordResultModel>>(
    `lite/job/v2/dock/check_struct_condition_items`,
    {
      ...params,
    }
  );
};

export const convertPdbToCifApi = (params: FileContentConvertPayloadModel) => {
  return http.post<null, Page<FileContentConvertResultModel>>(
    `lite/file/pdb_content_to_cif`,
    {
      ...params,
    }
  );
};

export const convertAntibodyDesignParamsApi = (
  params: AntibodyDesignParamsConvertPayloadModel
) => {
  return http.post<null, Page<null>>(
    `lite/job/v2/antibody_design/params_to_geoflowv3`,
    {
      ...params,
    }
  );
};

export const createPeptideDesignApi = async (
  params: PeptideDesignPayloadModel
) => {
  return http.post<null, Page<{ job_id: string }>>(
    `lite/job/v2/peptide_design/create`,
    {
      ...params,
    }
  );
};

export const getPeptideDesignTokenInfoApi = async (
  params: PeptideDesignPayloadModel
) => {
  const { data } = await request.post<
    null,
    Page<{ token_info: TokenInfoModel }>
  >(`lite/job/v2/peptide_design/token_info`, {
    ...params,
  });

  return data.token_info;
};
